import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';

interface AutoShift {
  id: string;
  code: string;
  name: string;
  clock_in_from: string;
  clock_in_to: string;
  shift_id: string;
  shift_name: string;
}
interface Shift {
  id: string;
  code: string;
  name: string;
}

const EMPTY = { code: '', name: '', clockInFrom: '', clockInTo: '', shiftId: '' };

export default function AutoShifts() {
  const [rows, setRows] = useState<AutoShift[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<AutoShift | null>(null);
  const [error, setError] = useState('');

  const load = async () => {
    const [a, s] = await Promise.all([api.get('/tms/auto-shifts'), api.get('/tms/shifts')]);
    setRows(a.data.autoShifts || []);
    setShifts(s.data.shifts || []);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: AutoShift) => {
    setEditingId(row.id);
    setForm({ code: row.code, name: row.name, clockInFrom: row.clock_in_from || '', clockInTo: row.clock_in_to || '', shiftId: row.shift_id });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await api.put(`/tms/auto-shifts/${editingId}`, form);
      } else {
        await api.post('/tms/auto-shifts', form);
      }
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/tms/auto-shifts/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Auto Shift</h1>
        <Button onClick={openCreate}>+ New</Button>
      </div>
      <Table
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          { key: 'clock_in_from', header: 'Clock In From' },
          { key: 'clock_in_to', header: 'Clock In To' },
          { key: 'shift_name', header: 'Shift' },
          {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(row)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
        rows={rows}
      />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Auto Shift' : 'New Auto Shift'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Clock In From" type="time" required value={form.clockInFrom} onChange={(e) => setForm({ ...form, clockInFrom: e.target.value })} />
          <Input label="Clock In To" type="time" required value={form.clockInTo} onChange={(e) => setForm({ ...form, clockInTo: e.target.value })} />
          <Select
            label="Shift"
            required
            options={shifts.map((s) => ({ value: s.id, label: s.name }))}
            value={form.shiftId}
            onChange={(e) => setForm({ ...form, shiftId: e.target.value })}
          />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        message={`Delete auto shift "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
